import { ActionIcon, Button, Pagination, Select, TextInput } from '@mantine/core';
import {
  IconHistory,
  IconPackageOff,
  IconPackages,
  IconPencil,
  IconPlus,
  IconRefresh,
  IconSearch,
} from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import { errorCode } from '../personnel/source.js';
import type { PersonnelVessel } from '../personnel/types.js';
import { StockStatusBadge } from './badges.js';
import { filterStockItems, formatMoney, formatQuantity, sortStockItems } from './inventory.js';
import { TableSkeleton } from './MaterialMasterScreen.js';
import classes from './Materials.module.css';
import { DEFAULT_STOCK_SORT, SortTh, type StockSortState } from './SortHeader.js';
import stock from './Stock.module.css';
import { ItemHistoryDialog, StockItemDialog } from './StockDialogs.js';
import type { MaterialsSource } from './source.js';
import type { StockItem, VesselStock } from './types.js';

/** Sayfa boyutları (G5-25); varsayılan 10 (`satin-alma-akisi.jpeg`). */
const PAGE_SIZES = [10, 20, 50] as const;

type State =
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | { kind: 'ready'; vessels: PersonnelVessel[]; stock: VesselStock | null };

export interface StockManagementScreenProps {
  source: MaterialsSource;
}

/**
 * Stok yönetimi (`satin-alma-akisi.jpeg` "Stock Management"; ADR 0026 §1–2): gemi, kategori ve
 * malzeme süzgeçleri; eldeki, en az, en çok ve durum. Gemide kaptan ve başmühendis stok kalemi
 * açar ve seviyeleri düzeltir; ofiste salt okunur (gemi verisi, mimari belge §8). "Add Stock Item"
 * süzgeç satırının başında ("+ New Requisition" düzeni) ve "Actions" sütunu tasarımdan bilinçli
 * farklardır (M12 planı).
 */
export function StockManagementScreen({ source }: StockManagementScreenProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'en';
  const k = (key: string, options: Record<string, unknown> = {}) => t(`stock.${key}`, options);
  const [vesselId, setVesselId] = useState<string | null>(null);
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [draft, setDraft] = useState({ mainCategory: '', q: '' });
  const [filter, setFilter] = useState({ mainCategory: '', q: '' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZES[0]);
  const [sort, setSort] = useState<StockSortState>(DEFAULT_STOCK_SORT);
  const [editing, setEditing] = useState<StockItem | 'new' | null>(null);
  const [history, setHistory] = useState<StockItem | null>(null);

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      const vessels = await source.vessels();
      const vessel = vessels.find((v) => v.id === vesselId) ?? vessels[0];
      setState({
        kind: 'ready',
        vessels,
        stock: vessel ? await source.stock(vessel.id) : null,
      });
    } catch (error) {
      setState({ kind: 'error', code: errorCode(error) });
    }
  }, [source, vesselId]);
  useEffect(() => {
    void load();
  }, [load]);

  const ready = state.kind === 'ready' ? state : null;
  const items = ready?.stock?.items ?? [];
  const mains = useMemo(() => [...new Set(items.map((i) => i.mainCategory))], [items]);
  const view = useMemo(
    () =>
      sortStockItems(
        filterStockItems(items, { q: filter.q, mainCategory: filter.mainCategory || null }),
        sort.column,
        locale,
        sort.direction,
      ),
    [items, filter, sort, locale],
  );
  const sortTh = (column: StockSortState['column'], label: string) => (
    <SortTh
      column={column}
      label={label}
      sort={sort}
      onSort={(next) => {
        setSort(next);
        setPage(1);
      }}
    />
  );
  const pages = Math.max(1, Math.ceil(view.length / pageSize));
  const current = Math.min(page, pages);
  const visible = view.slice((current - 1) * pageSize, current * pageSize);
  const editor = source.stockEditor;
  const n = (value: number | null) => (value === null ? '—' : formatQuantity(value, locale));
  const money = (value: number | null | undefined, currency: string | null | undefined) =>
    formatMoney(value, currency, locale);
  const vessel = ready?.vessels.find((v) => v.id === ready.stock?.vesselId) ?? null;
  const filtered = Boolean(filter.q.trim() || filter.mainCategory);
  const clearFilters = () => {
    const empty = { mainCategory: '', q: '' };
    setDraft(empty);
    setFilter(empty);
    setPage(1);
  };

  return (
    <div className={classes.screen}>
      <PageHeader title={k('management.title')} icon={<IconPackages stroke={1.6} />} />

      <Panel flush className={stock.managementCard} aria-label={k('management.title')}>
        <form
          className={stock.filters}
          data-editor={editor ? true : undefined}
          aria-label={k('filters')}
          onSubmit={(event) => {
            event.preventDefault();
            setFilter(draft);
            setPage(1);
          }}
        >
          {editor ? (
            <Button leftSection={<IconPlus size={16} />} onClick={() => setEditing('new')}>
              {k('management.add')}
            </Button>
          ) : null}
          {source.scope === 'ship' ? (
            // Gemide tek gemi: salt okunur alan, gri (disabled) değil (plan §0.6; G5-20)
            <TextInput
              label={k('vessel')}
              readOnly
              value={vessel?.name ?? ''}
              placeholder={ready ? '—' : k('loading')}
            />
          ) : (
            <Select
              label={k('vessel')}
              data={(ready?.vessels ?? []).map((v) => ({ value: v.id, label: v.name }))}
              value={vessel?.id ?? null}
              placeholder={ready ? undefined : k('loading')}
              allowDeselect={false}
              onChange={(value) => {
                setVesselId(value);
                setPage(1);
              }}
            />
          )}
          <Select
            label={k('category')}
            data={[
              { value: '', label: k('allCategories') },
              ...mains.map((value) => ({ value, label: value })),
            ]}
            value={draft.mainCategory}
            allowDeselect={false}
            onChange={(value) => setDraft((d) => ({ ...d, mainCategory: value ?? '' }))}
          />
          <TextInput
            label={k('material')}
            placeholder={k('searchMaterial')}
            leftSection={<IconSearch size={16} aria-hidden />}
            value={draft.q}
            onChange={(event) => {
              const q = event.currentTarget.value;
              setDraft((d) => ({ ...d, q }));
            }}
          />
          <Button type="submit" className={stock.searchButton}>
            {k('search')}
          </Button>
        </form>

        <div className={stock.tableBox} aria-busy={state.kind === 'loading'}>
          {state.kind === 'error' ? (
            <EmptyState
              tone="danger"
              title={k('error')}
              action={
                <Button
                  size="xs"
                  variant="light"
                  leftSection={<IconRefresh size={14} />}
                  onClick={() => void load()}
                >
                  {k('retry')}
                </Button>
              }
            />
          ) : state.kind === 'loading' ? (
            <TableSkeleton columns={8} rows={PAGE_SIZES[0]} />
          ) : items.length === 0 ? (
            <EmptyState
              icon={<IconPackageOff size={22} stroke={1.8} />}
              title={k('emptyTitle')}
              // "Add Stock Item" hemen üstte, süzgeç satırında; boş durumda ikinci düğme yok
              description={editor ? k('emptyShip') : k('emptyWeb')}
            />
          ) : view.length === 0 ? (
            <EmptyState
              icon={<IconSearch size={22} stroke={1.8} />}
              title={k('noMatch')}
              action={
                filtered ? (
                  <Button size="xs" variant="default" onClick={clearFilters}>
                    {t('common.clearFilters')}
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <section
              className={classes.tableWrap}
              aria-label={k('management.table')}
              // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
              tabIndex={0}
            >
              {/* Sütun oranları referanstan (G5-18); sayısallar referanstaki gibi sola yaslı */}
              <table className={`${classes.table} ${stock.managementTable}`}>
                <colgroup>
                  {[
                    'material',
                    'category',
                    'sub',
                    'uom',
                    'onHand',
                    'min',
                    'max',
                    'price',
                    'value',
                    'status',
                  ].map((name) => (
                    <col key={name} data-col={name} />
                  ))}
                  <col data-col="actions" />
                </colgroup>
                <thead>
                  <tr>
                    {sortTh('name', k('columns.material'))}
                    {sortTh('category', k('columns.category'))}
                    <th scope="col" data-col="sub">
                      {k('columns.subCategory')}
                    </th>
                    <th scope="col">{k('columns.uom')}</th>
                    {sortTh('onHand', k('columns.onHand'))}
                    <th scope="col">{k('columns.minStock')}</th>
                    <th scope="col" data-col="max">
                      {k('columns.maxStock')}
                    </th>
                    {/* Son alış fiyatı ve değer (ortağın notu #11; ADR 0047 §5) */}
                    <th scope="col">{k('columns.lastPrice')}</th>
                    <th scope="col">{k('columns.value')}</th>
                    {sortTh('status', k('columns.status'))}
                    <th scope="col">{k('columns.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((item) => (
                    <tr key={item.id}>
                      <td className={classes.itemName}>{item.name}</td>
                      <td className={classes.secondary}>{item.mainCategory}</td>
                      <td className={classes.secondary} data-col="sub">
                        {item.subCategory}
                      </td>
                      <td>{item.unit}</td>
                      <td data-numeric>{n(item.onHand)}</td>
                      <td data-numeric>{n(item.minQty)}</td>
                      <td data-numeric data-col="max">
                        {n(item.maxQty)}
                      </td>
                      <td data-numeric>{money(item.lastUnitPrice, item.lastCurrency)}</td>
                      <td data-numeric>{money(item.value, item.lastCurrency)}</td>
                      <td>
                        <StockStatusBadge status={item.status} variant="management" />
                      </td>
                      <td className={stock.actions}>
                        <ActionIcon
                          variant="subtle"
                          size="sm"
                          aria-label={k('historyOf', { name: item.name })}
                          onClick={() => setHistory(item)}
                        >
                          <IconHistory size={16} />
                        </ActionIcon>
                        {editor ? (
                          <ActionIcon
                            variant="subtle"
                            size="sm"
                            aria-label={k('editLevels', { name: item.name })}
                            onClick={() => setEditing(item)}
                          >
                            <IconPencil size={16} />
                          </ActionIcon>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}
        </div>

        {view.length > 0 && ready ? (
          <div className={stock.managementPager}>
            <span>
              {k('showing', {
                from: (current - 1) * pageSize + 1,
                to: Math.min(current * pageSize, view.length),
                total: view.length,
              })}
            </span>
            <div className={classes.pagerEnd}>
              {pages > 1 ? (
                <Pagination
                  total={pages}
                  value={current}
                  onChange={setPage}
                  aria-label={k('pagination')}
                  getControlProps={(control) => ({ 'aria-label': t(`materials.page.${control}`) })}
                  getItemProps={(number) => ({
                    'aria-label': t('materials.page.number', { page: number }),
                  })}
                />
              ) : null}
              {view.length > PAGE_SIZES[0] ? (
                <Select
                  size="xs"
                  className={classes.pageSize}
                  aria-label={k('pageSize')}
                  data={PAGE_SIZES.map((size) => ({
                    value: String(size),
                    label: k('perPage', { size }),
                  }))}
                  value={String(pageSize)}
                  allowDeselect={false}
                  onChange={(value) => {
                    setPageSize(Number(value ?? PAGE_SIZES[0]));
                    setPage(1);
                  }}
                />
              ) : null}
            </div>
          </div>
        ) : null}
      </Panel>

      {editor ? (
        <StockItemDialog
          opened={editing !== null}
          item={editing === 'new' ? null : editing}
          source={source}
          editor={editor}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load();
          }}
        />
      ) : null}
      <ItemHistoryDialog item={history} source={source} onClose={() => setHistory(null)} />
    </div>
  );
}
